import { type MutableRefObject, useCallback, useEffect, useState } from 'react';

export const useInputCopy = ({ canCopy, inputRef }: { canCopy: boolean; inputRef: MutableRefObject<HTMLInputElement | null> }) => {
  const [isCopyTooltipOpen, setIsCopyTooltipOpen] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => setIsCopyTooltipOpen(false), 3000);
    return () => clearTimeout(timeout);
  }, [isCopyTooltipOpen]);

  const handleCopy = useCallback(() => {
    if (!canCopy) return;
    if (!inputRef.current) return;

    if (navigator?.clipboard) {
      navigator.clipboard.writeText(inputRef.current.value);
      inputRef.current.blur();
      setIsCopyTooltipOpen(true);
    } else {
      inputRef.current.select();
      inputRef.current.setSelectionRange(0, 99999); // For mobile devices
    }
  }, [canCopy]);

  return { isCopyTooltipOpen, handleCopy };
};

interface UseValueCopyParam {
  value: string;
  elementRef: MutableRefObject<HTMLElement | null>;
}

export const useValueCopy = ({ value, elementRef }: UseValueCopyParam) => {
  const [isCopyTooltipOpen, setIsCopyTooltipOpen] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => setIsCopyTooltipOpen(false), 3000);
    return () => clearTimeout(timeout);
  }, [isCopyTooltipOpen]);

  const handleCopy = useCallback(() => {
    if (!value) return;

    if (navigator?.clipboard) {
      navigator.clipboard.writeText(value);
      setIsCopyTooltipOpen(true);

      const selection = window.getSelection();
      if (selection) selection.removeAllRanges();
    } else {
      if (!elementRef.current) return;

      if (window.getSelection) {
        const selection = window.getSelection();
        if (!selection) return;
        const range = document.createRange();
        range.selectNodeContents(elementRef.current);
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
  }, [value]);

  return { isCopyTooltipOpen, handleCopy };
};
